<script lang="ts">
	import {
		PUBLIC_BIXOTO_MEDIA,
		PUBLIC_PROD,
		PUBLIC_SITE_URL,
		PUBLIC_STRAPI_MEDIA,
		PUBLIC_ANALYTICS_TAG_ID,
	} from '$env/static/public'
	import { page } from '$app/state'
	import RemoveModal from '$lib/modals/RemoveModal.svelte'
	import StockModal from '$lib/modals/StockModal.svelte'
	import ErrorModal from '$lib/modals/ErrorModal.svelte'
	import LoginModal from '$lib/components/account/LoginModal.svelte'
	import SiteHeader from '$lib/components/common/SiteHeader.svelte'
	import SiteFooter from '$lib/components/common/SiteFooter.svelte'
	import CookieNotice from '$lib/components/common/CookieNotice.svelte'
	import Analytics from '$lib/components/common/Analytics.svelte'
	import MiniCart from '$lib/components/cart/MiniCart.svelte'
	import MobileMenu from '$lib/components/common/menu/MobileMenu.svelte'
	import SearchModal from '$lib/modals/SearchModal.svelte'
	import ChatModal from '$lib/modals/ChatModal.svelte'
	import {
		storeCartError,
		storeChatModal,
		storeCheckoutIframe,
		storeCookieSelection,
		storeMobileMenu,
		storeLoginModal,
		storeRemoveModal,
		storeSearchedText,
		storeSearchModal,
		storeSnackbar,
		storeStockModal,
	} from '$lib/js/stores'
	import { getMetaDescription, getPageTitle } from '$lib/js/seo'
	import { locale } from 'svelte-i18n-lingui'
	import { getWebsiteSchema } from '$lib/js/structured_data'
	import { sendWT3 } from '$lib/js/helpers/wt3'
	import type { Page } from '@sveltejs/kit'
	/* Structured data */
	import StructuredData from '$lib/components/common/StructuredData.svelte'
	import { onMount, setContext } from 'svelte'
	import { afterNavigate, beforeNavigate } from '$app/navigation'
	import Snackbar from '$lib/components/common/Snackbar.svelte'
	import ChatIcon from '$lib/components/common/ChatIcon.svelte'
	import { loadChatwoot } from '$lib/js/chatwoot/helpers'
	import { onNavigate } from '$app/navigation'
	import { storeCustomerInfo, storeCustomerToken, storeCustomerTokenSet } from '$lib/js/stores'
	import { get } from 'svelte/store'

	interface Props {
		data: import('./$types').PageData
		children?: import('svelte').Snippet
	}

	let { data, children }: Props = $props()

	locale.set(data.i18n.lang, data.i18n.messages)

	const scope = data.scope
	setContext('scope', scope)

	let loaded: boolean = $state(false)
	let old_search = $state('')

	/* Set active page in menu */
	let pageName = page.route.id || ''
	let uClass = $derived(() => {
		let theClass = ''

		if (page?.route?.id?.includes('p/[prod_url]')) {
			theClass = 'product-page'
		} else if (
			page?.route?.id?.includes('c/[...cat_url]') ||
			page?.route?.id?.includes('brands/[brand_slug]')
		) {
			theClass = 'category-page'
		}

		return theClass
	})

	/* Add meta description */
	let meta_title = $derived(getPageTitle(page as Page))
	let meta_desc = $derived(getMetaDescription(page as Page))
	let page_url = $state(page.url.href || '')

	let og_image = $state(PUBLIC_SITE_URL + '/images/og_bx.jpg')
	let img_height = '480'
	let img_width = $state('960')
	let og_type = $derived(pageName.includes('/blog/') ? 'article' : 'website')
	let wt3Sent = $state(null)
	setOgValues()

	function setOgValues() {
		if (pageName.includes('p/[prod_url]')) {
			og_image = PUBLIC_BIXOTO_MEDIA + page.data.product?.image
			img_width = '480'
		} else if (pageName.includes('/blog/[post_url]') || pageName.includes('/[static]')) {
			og_image = PUBLIC_STRAPI_MEDIA + page.data.page_data?.hero_section?.bg_img?.url
		} else if (pageName.includes('/u/[...universe_url]') && page.data.universe) {
			og_image = `${PUBLIC_SITE_URL}/images/universes/meta/meta_${page.data.universe.universe_url}.jpg`
		} else {
			og_image = PUBLIC_SITE_URL + '/images/og_bx.jpg'
			img_width = '960'
		}
	}

	onMount(() => {
		loaded = true

		loadChatwoot()
			.catch((err) => console.error(err))
			.then(() => {
				window.addEventListener('chatwoot:widget:closed', () => {
					console.log('CLOSING WIDGET')
				})
			})
	})

	$effect(() => {
		if (page.url.href) {
			if (page.url.searchParams.get('search') && loaded) {
				let search = page.url.searchParams.get('search')
				if (search != old_search) {
					$storeSearchedText = search
				}
				$storeSearchModal = true
				old_search = search
			}
		}
	})

	onNavigate(() => {
		meta_title = getPageTitle(page as Page)
		meta_desc = getMetaDescription(page as Page)
		page_url = page.url.href || ''
	})

	beforeNavigate(() => {
		pageName = page.route.id || ''
		setOgValues()
	})

	afterNavigate(async () => {
		let search = page.url.searchParams.get('search')
		if (search) {
			$storeSearchModal = true
			$storeSearchedText = page.url.searchParams.get('search')
		}
		wt3Sent = false
	})
	$effect(() => {
		// The effect automatically tracks $myStore and hasActionRun as dependencies.
		if ($storeCustomerTokenSet && wt3Sent === false) {
			sendWT3()
			wt3Sent = true
		}
	})
</script>

<svelte:head>
	<title>{meta_title}</title>
	<meta name="title" content={meta_title} />
	<meta name="description" content={meta_desc} />
	<meta
		name="keywords"
		content="online store, sustainable products, eco-friendly, Bixoto, {meta_title}"
	/>
	<link rel="canonical" href={page_url || ''} />

	<!-- Hreflang tags for internationalization -->
	<link rel="alternate" hreflang="es" href={page_url || ''} />
	<link rel="alternate" hreflang="en" href={page_url?.replace('/es/', '/en/') || ''} />
	<link rel="alternate" hreflang="x-default" href={page_url?.replace('/en/', '/es/') || ''} />

	<link rel="apple-touch-icon" sizes="180x180" href="/images/favicon/apple-touch-icon.png" />
	<link
		rel="icon"
		type="image/png"
		sizes="192x192"
		href="/images/favicon/android-chrome-192x192.png"
	/>
	<link
		rel="icon"
		type="image/png"
		sizes="512x512"
		href="/images/favicon/android-chrome-512x512.png"
	/>
	<link rel="icon" type="image/png" sizes="16x16" href="/images/favicon/favicon-16x16.png" />
	<link rel="icon" type="image/png" sizes="32x32" href="/images/favicon/favicon-32x32.png" />
	<link rel="icon" href="/images/favicon/favicon.ico" />
	<link rel="manifest" href="/images/favicon/manifest.json" />

	<meta property="og:site_name" content="Bixoto.com" />
	<meta property="og:title" content={meta_title} />
	<meta property="og:locale" content="es-ES" />
	<meta property="og:description" content={meta_desc} />
	<meta property="og:url" content={page_url || ''} />
	<meta property="og:type" content={og_type} />
	<meta property="og:image" content={og_image} />
	<meta property="og:image:secure_url" content={og_image} />
	<meta property="og:image:alt" content={meta_title} />
	<meta property="og:image:width" content={img_width} />
	<meta property="og:image:height" content={img_height} />
	<meta property="twitter:card" content="summary_large_image" />
	<meta property="twitter:site" content="@bixoto_com" />
	<meta property="twitter:creator" content="@bixoto_com" />
	<meta property="twitter:title" content={meta_title} />
	<meta property="twitter:description" content={meta_desc} />
	<meta property="twitter:image" content={og_image} />
	<meta property="twitter:image:alt" content={meta_title} />

	<!--suppress JSUnresolvedReference -->
	<script async src="https://www.googletagmanager.com/gtag/js?id={PUBLIC_ANALYTICS_TAG_ID}">
	</script>
	<script>
		window.dataLayer = window.dataLayer || []

		function gtag() {
			dataLayer.push(arguments)
		}

		gtag('js', new Date())
		gtag('consent', 'default', {
			ad_storage: 'denied',
			analytics_storage: 'denied',
			ad_user_data: 'denied',
			ad_personalization: 'denied',
		})
	</script>
	<!-- Meta Pixel Code -->
	<script>
		!(function (f, b, e, v, n, t, s) {
			if (f.fbq) return
			n = f.fbq = function () {
				n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
			}
			if (!f._fbq) f._fbq = n
			n.push = n
			n.loaded = !0
			n.version = '2.0'
			n.queue = []
			t = b.createElement(e)
			t.async = !0
			t.src = v
			s = b.getElementsByTagName(e)[0]
			s.parentNode.insertBefore(t, s)
		})(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
		fbq('init', '380595706402001')
		fbq('track', 'PageView')
	</script>
	<noscript
		><img
			height="1"
			width="1"
			alt=""
			style="display:none"
			src="https://www.facebook.com/tr?id=380595706402001&ev=PageView&noscript=1"
		/></noscript
	>
	<!-- End Meta Pixel Code -->
</svelte:head>

<div
	style="
	--color-main: #ff4200;
	--color-secondary: #34c9a2;
	--color-grey: #f0f0f0;
	--color-grey--b: #606060;
  --mdc-theme-secondary: #ff4200;
  --mdc-checkbox-ink-color: #ffffff;
	"
	class="app-content {uClass()}"
>
	<div class="app-content__inner">
		<SiteHeader />

		<main class="main-content">
			{@render children?.()}
		</main>
	</div>

	<SiteFooter />
	<ChatIcon />

	{#if $storeChatModal}
		<ChatModal />
	{/if}

	{#if !page.url.pathname.includes('checkout/cart')}
		<MiniCart />
	{/if}

	{#if !$storeCookieSelection || $storeCookieSelection === 'not-selected'}
		<CookieNotice />
	{/if}

	{#if $storeRemoveModal}
		<RemoveModal />
	{/if}

	{#if $storeLoginModal}
		<LoginModal />
	{/if}

	{#if $storeStockModal !== ''}
		<StockModal />
	{/if}

	{#if $storeCartError}
		<ErrorModal />
	{/if}

	{#if $storeMobileMenu}
		<MobileMenu />
	{/if}

	{#if $storeSearchModal}
		<SearchModal />
	{/if}
</div>

<StructuredData schema={getWebsiteSchema({ scope: scope })} />
{#if PUBLIC_PROD === '1'}
	<Analytics />
{/if}

{#if $storeCheckoutIframe === true && PUBLIC_PROD === '1'}
	<iframe src="/{scope}/checkout?no_tracking=true" id="checkout-iframe"></iframe>
{/if}

{#if $storeSnackbar.show}
	<Snackbar message={$storeSnackbar.message} />
{/if}

<style lang="scss">
	#checkout-iframe {
		display: none;
	}
</style>
